import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  CircularProgress,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dataService from "@/app/services/dataService";
import { useSelector } from "react-redux";
import PageLoading from "@/app/components/shared/pageLoading";
import checkIcon from "../../../img/check-icon-green.png";
import {
  UserProfileResponse,
  UserProfileRequest,
  UpdatePasswordRequest,
  ValidateEmailRequest,
} from "@/app/models/accountProfile/types";
import { RootState } from "@/app/store/store";
import { useGlobalNotification } from "@/app/components/header/GlobalNotificationContext";
import HomepageSecondaryUser from "../home/homepageSecondaryUser";
import BorderedInputLansing from "@/app/components/shared/BorderedInput";
import { useNavigate } from "react-router-dom";
import CommonHelper from "@/app/helpers/CommonHelper";

export default function UserProfile() {
  const userId = useSelector(
    (state: RootState) => state.userState.user.id ?? 0
  );
  const isSecondaryUser = useSelector(
    (state: RootState) => state.userState.user.isSecondaryUser
  );

  const isInternal = useSelector(
    (state: RootState) => state.userState.user.isInternal
  );

  const { setNotification } = useGlobalNotification();
  const navigate = useNavigate();

  const [successOpen, setSuccessOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState<UserProfileResponse>();
  const [error, setError] = useState<string | null>(null);
  const [emailServerError, setEmailServerError] = useState<string | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");

  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    form: "",
  });

  const [pwdErrors, setPwdErrors] = useState({
    oldPwd: "",
    newPwd: "",
    confirmPwd: "",
    form: "",
  });

  const toDigits = (s: string | null | undefined) =>
    (s ?? "").replace(/\D/g, "").slice(0, 10);

  const isEmail = (s: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s);

  const hydrateFromModel = (m?: UserProfileResponse | null) => {
    setFirstName(m?.firstName ?? "");
    setLastName(m?.lastName ?? "");
    setEmail(m?.email ?? "");
    setMobilePhone(CommonHelper.formatPhone334(m?.phone ?? ""));
    setErrors({ firstName: "", lastName: "", email: "", phone: "", form: "" });
    setEmailServerError(null);
    setIsCheckingEmail(false);
  };

  useEffect(() => {
    if (!editOpen) {
      setEmailServerError(null);
      setIsCheckingEmail(false);
      return;
    }

    const trimmed = email.trim();

    if (!trimmed) {
      setEmailServerError(null);
      setIsCheckingEmail(false);
      return;
    }

    if (!isEmail(trimmed)) {
      setEmailServerError(null);
      setIsCheckingEmail(false);
      return;
    }

    const payload: ValidateEmailRequest = {
      email: trimmed,
      customerId: userId ?? 0,
    };

    setIsCheckingEmail(true);
    const handle = setTimeout(async () => {
      try {
        const res = await dataService.validateSecondaryUserEmail(payload);

        if (res?.success && res.data?.success) {
          setEmailServerError(null);
        } else {
          setEmailServerError(
            res?.data?.message || "This email is already in use."
          );
        }
      } catch (err: any) {
        setEmailServerError(
          err?.response?.data?.message || "Unable to validate email."
        );
      } finally {
        setIsCheckingEmail(false);
      }
    }, 500);

    return () => clearTimeout(handle);
  }, [editOpen, email, userId]);

  const loadProfile = useCallback(async () => {
    setPageLoading(true);
    setError(null);
    try {
      const res = await dataService.getUserProfile(userId);
      if (res?.success && res.data) {
        setModel(res.data as UserProfileResponse);
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load preferences."
      );
    } finally {
      setPageLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadProfile();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadProfile]);

  if (pageLoading) return <PageLoading />;

  const profile = model ?? null;

  const handleEdit = () => {
    hydrateFromModel(model);
    setEditOpen(true);
  };
  const handleCloseEdit = () => setEditOpen(false);

  const resetPwdForm = () => {
    setOldPwd("");
    setNewPwd("");
    setConfirmPwd("");
    setPwdErrors({ oldPwd: "", newPwd: "", confirmPwd: "", form: "" });
  };
  const handleChangePassword = () => {
    resetPwdForm();
    setPwdOpen(true);
  };

  const handleClosePwd = () => {
    setPwdOpen(false);
    resetPwdForm();
  };

  const validateEdit = () => {
    const next = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      form: "",
    };

    if (!firstName.trim()) next.firstName = "Required";
    if (!lastName.trim()) next.lastName = "Required";

    if (!email.trim()) next.email = "Required";
    else if (!isEmail(email)) next.email = "Wrong email";

    if (mobilePhone.trim()) {
      const d = toDigits(mobilePhone);
      if (d.length !== 10) next.phone = "Enter a 10-digit number";
    }

    const hasErrors = Object.values(next).some(Boolean);
    if (hasErrors) next.form = "Please fill in the required fields.";

    setErrors(next);
    return !hasErrors;
  };

  const handleSubmitEdit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateEdit()) return;
    setLoading(true);
    const cleanPhone = toDigits(mobilePhone);
    const payload: UserProfileRequest = {
      firstName,
      lastName,
      email,
      phone: cleanPhone,
    };

    try {
      const res = await dataService.updateUserProfile(payload, userId);
      if (!res.success) {
        setEditOpen(false);
        setNotification(res.error, "error");
        return;
      }

      setModel((prev) => ({
        ...(prev ?? {
          customerId: 0,
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
        }),
        firstName,
        lastName,
        email,
        phone: cleanPhone,
      }));

      setSuccessOpen(true);
    } catch {
      setNotification("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
      setEditOpen(false);
    }
  };

  const validatePwd = () => {
    const next = { oldPwd: "", newPwd: "", confirmPwd: "", form: "" };

    if (!oldPwd.trim()) next.oldPwd = "Old password is required";
    if (!newPwd.trim()) next.newPwd = "New password is required";

    if (!confirmPwd.trim()) {
      next.confirmPwd = "Confirm password is required";
    } else if (newPwd && confirmPwd && newPwd !== confirmPwd) {
      next.confirmPwd = "Passwords do not match";
    }

    const hasErrors = Object.values(next).some(Boolean);

    setPwdErrors(next);
    return !hasErrors;
  };

  const handleSubmitPwd = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validatePwd()) return;
    setLoading(true);
    const payload: UpdatePasswordRequest = {
      newPassword: newPwd,
      confirmNewPassword: confirmPwd,
      oldPassword: oldPwd,
    };

    try {
      const res = await dataService.updateUserProfilePassword(payload);
      if (!res.success) {
        setEditOpen(false);
        setNotification(res.error, "error");
        setPwdOpen(false);
        return;
      }
      setSuccessOpen(true);
      setPwdOpen(false);
      setOldPwd("");
      setNewPwd("");
      setConfirmPwd("");
      setPwdErrors({ oldPwd: "", newPwd: "", confirmPwd: "", form: "" });
    } catch {
      setNotification("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            pointerEvents: "auto",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#1976d2" }} />
        </Box>
      ) : (
        <></>
      )}

      <Box sx={{ background: "#f9fafb", pb: 2, minHeight: "100vh" }}>
        {isSecondaryUser && <HomepageSecondaryUser />}
        <Box
          sx={{
            backgroundColor: "#2C7EAC",
            marginBottom: "50px",
            minHeight: "100px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              fontSize: "36px",
            }}
          >
            User Profile
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 4,
            px: 4,
            mx: "auto",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={5.5}>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                LansingNOW User Profile
              </Typography>
              <Typography sx={{ mt: 2 }}>
                Manage your online LansingNOW profile information here.
              </Typography>
              <Stack alignItems="center" width={"100%"} sx={{ mt: 5 }}>
                <ProfileRow
                  label="First Name:"
                  value={profile?.firstName ?? ""}
                />
                <ProfileRow
                  label="Last Name:"
                  value={profile?.lastName ?? ""}
                />
                <ProfileRow
                  label="Username (Email):"
                  value={profile?.email ?? ""}
                />
                <ProfileRow
                  label="Mobile Phone:"
                  value={
                    CommonHelper.formatPhone334(profile?.phone ?? "") || ""
                  }
                />
              </Stack>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                  mt: 3,
                }}
              >
                <Button className="darkBlueButtonSolid" onClick={handleEdit}>
                  Edit Profile
                </Button>
                <Button
                  className="darkBlueButtonOutline"
                  onClick={handleChangePassword}
                >
                  Change Password
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={0.5}></Grid>
            {!isInternal && (
              <Grid item xs={12} md={6}>
                <>
                  <Typography sx={{ fontWeight: "bold", mt: 1 }}>
                    Looking for your Lansing Customer Account information?
                  </Typography>
                  <Typography sx={{ mt: 2 }}>
                    Click{" "}
                    <Link
                      component="button"
                      underline="hover"
                      sx={{
                        fontWeight: 600,
                        color: "#0057B8",
                        cursor: "pointer",
                        display: "inline",
                        verticalAlign: "baseline",
                      }}
                      onClick={() => navigate("/info")}
                    >
                      here
                    </Link>{" "}
                    to view your Lansing Account details.
                  </Typography>
                </>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>

      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        fullWidth
        maxWidth="sm"
        scroll="body"
      >
        <DialogTitle textAlign="center" fontWeight={700}>
          <Typography
            textAlign="center"
            fontWeight={700}
            sx={{ fontSize: "16.8px" }}
          >
            LANSINGNOW USER PROFILE
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseEdit}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              mb: 1.5,
              fontStyle: "italic",
              color: "text.secondary",
              fontSize: "14px",
              fontFamily: '"Montserrat", Verdana, sans-serif',
            }}
          >
            You can update and maintain this information here.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ overflow: "visible" }}>
          <form id="edit-profile-form" onSubmit={handleSubmitEdit} noValidate>
            <Box sx={{ my: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                First Name:
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    color: "red",
                  }}
                >
                  *
                </Box>
              </Typography>
              <Box>
                <BorderedInputLansing
                  name="companyName"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (errors.firstName)
                      setErrors((s) => ({ ...s, firstName: "" }));
                  }}
                  error={!!errors.firstName}
                />
              </Box>
            </Box>
            <Box sx={{ my: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Last Name:
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    color: "red",
                  }}
                >
                  *
                </Box>
              </Typography>
              <Box>
                <BorderedInputLansing
                  name="companyName"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (errors.lastName)
                      setErrors((s) => ({ ...s, lastName: "" }));
                  }}
                  error={!!errors.lastName}
                />
              </Box>
            </Box>

            <Box sx={{ my: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Username (Email):
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    color: "red",
                  }}
                >
                  *
                </Box>
              </Typography>
              <Box>
                <BorderedInputLansing
                  name="companyName"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((s) => ({ ...s, email: "" }));
                    if (emailServerError) setEmailServerError(null);
                  }}
                  error={!!errors.email || !!emailServerError}
                  helperText={errors.email || emailServerError || " "}
                />
              </Box>
            </Box>

            <Box sx={{ my: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Mobile Phone:
              </Typography>
              <Box>
                <BorderedInputLansing
                  name="mobilePhone"
                  value={mobilePhone}
                  onChange={(e) => {
                    setMobilePhone(CommonHelper.formatPhone334(e.target.value));
                    if (errors.phone) setErrors((s) => ({ ...s, phone: "" }));
                  }}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  inputMode="numeric"
                />
              </Box>
            </Box>

            <Stack spacing={3} mt={1}>
              {errors.form && (
                <Typography
                  sx={{ color: "error.main", textAlign: "center", mt: 1 }}
                >
                  {errors.form}
                </Typography>
              )}
            </Stack>
          </form>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            className="whiteButtonOutline"
            onClick={handleCloseEdit}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            className="blueButtonOutline filledBlueButton"
            type="submit"
            form="edit-profile-form"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pwdOpen}
        onClose={handleClosePwd}
        fullWidth
        maxWidth="sm"
        scroll="body"
      >
        <DialogTitle textAlign="center" fontWeight={700}>
          <Typography
            textAlign="center"
            fontWeight={700}
            sx={{ fontSize: "16.8px" }}
          >
            CHANGE PASSWORD
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClosePwd}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, overflow: "visible" }}>
          <form id="change-password-form" onSubmit={handleSubmitPwd} noValidate>
            <Box sx={{ my: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Old Password:
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    color: "red",
                  }}
                >
                  *
                </Box>
              </Typography>
              <Box>
                <BorderedInputLansing
                  name="password"
                  type="password"
                  value={oldPwd}
                  onChange={(e) => {
                    setOldPwd(e.target.value);
                    if (pwdErrors.oldPwd)
                      setPwdErrors((s) => ({ ...s, oldPwd: "" }));
                  }}
                  error={!!pwdErrors.oldPwd}
                  helperText={pwdErrors.oldPwd}
                />
              </Box>
            </Box>

            <Box sx={{ my: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                New Password:
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    color: "red",
                  }}
                >
                  *
                </Box>
              </Typography>
              <Box>
                <BorderedInputLansing
                  name="newPassowrd"
                  type="password"
                  value={newPwd}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNewPwd(v);
                    if (pwdErrors.newPwd)
                      setPwdErrors((s) => ({ ...s, newPwd: "" }));
                    if (pwdErrors.confirmPwd && confirmPwd && v === confirmPwd)
                      setPwdErrors((s) => ({ ...s, confirmPwd: "" }));
                  }}
                  error={!!pwdErrors.newPwd}
                  helperText={pwdErrors.newPwd}
                />
              </Box>
            </Box>

            <Box sx={{ my: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Confirm Password:
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    color: "red",
                  }}
                >
                  *
                </Box>
              </Typography>
              <Box>
                <BorderedInputLansing
                  name="confirmPassword"
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => {
                    const v = e.target.value;
                    setConfirmPwd(v);
                    if (pwdErrors.confirmPwd) {
                      if (!!v && (!newPwd || v === newPwd)) {
                        setPwdErrors((s) => ({ ...s, confirmPwd: "" }));
                      }
                    }
                  }}
                  error={!!pwdErrors.confirmPwd}
                  helperText={pwdErrors.confirmPwd}
                />
              </Box>
            </Box>

            <Stack spacing={3} mt={1}>
              {pwdErrors.form && (
                <Typography
                  sx={{ color: "error.main", textAlign: "center", mt: 1 }}
                >
                  {pwdErrors.form}
                </Typography>
              )}
            </Stack>
          </form>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            className="whiteButtonOutline"
            sx={{ fontSize: "14px" }}
            type="submit"
            form="change-password-form"
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        fullWidth
        maxWidth="sm"
        scroll="body"
      >
        <DialogTitle sx={{ p: 0 }}>
          <IconButton
            aria-label="close"
            onClick={() => setSuccessOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            overflow: "visible",
            pt: 4,
            pb: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: 28,
              fontWeight: 600,
              color: "#494440",
              mb: 3,
              px: 2,
              mt: 4,
            }}
          >
            Your changes have been saved
            <br />
            successfully!
          </Typography>
          <img
            src={checkIcon}
            alt="Success"
            style={{ width: 150, height: 150 }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        mb: 2,
        pb: 2,
        width: "100%",
        borderBottom: 1,
        borderColor: "#ebebeb",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          textAlign: { xs: "center", sm: "center", md: "left" },
          fontSize: 14,
          color: "#494440",
          fontWeight: 600,
          fontFamily: '"Montserrat", Verdana, sans-serif',
          mr: 2,
          width: { xs: "100%", sm: "100%", md: "50%" },
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          textAlign: { xs: "center", sm: "center", md: "left" },
          fontSize: 14,
          color: "#494440",
          flex: 1,
          fontFamily: '"Montserrat", Verdana, sans-serif',
          width: { xs: "100%", sm: "100%", md: "50%" },
          wordBreak: "break-all",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
